import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Clock,
  User,
  Send,
} from 'lucide-react';
import RefreshButton from '../../components/layout/RefreshButton';
import { adminApi } from '../../services/adminApi';

export default function EmailsList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const agentId = searchParams.get('agent_id') || '';

  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [agentInfo, setAgentInfo] = useState(null);

  const fetchEmails = useCallback(async () => {
    if (!agentId) return;
    try {
      const response = await adminApi.getAgentEmails(agentId, {
        page,
        limit: 20,
      });
      setEmails(response.emails || []);
      setAgentInfo(response.agent);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch emails:', err);
    }
  }, [page, agentId]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchEmails();
      setIsLoading(false);
    };
    loadData();
  }, [fetchEmails]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchEmails();
    setIsRefreshing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!agentId) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto text-center">
        <p className="text-text-muted">No agent specified</p>
        <button onClick={() => navigate('/agents')} className="btn-primary mt-4">
          Back to Agents
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/agents/${agentId}`)}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Agent
      </button>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            {agentInfo ? `Emails - ${agentInfo.name}` : 'Emails'}
          </h1>
          <p className="text-text-secondary mt-1">
            View all emails for this agent ({total} total)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton
            onClick={handleRefresh}
            isLoading={isRefreshing}
            lastUpdated={lastUpdated}
          />
        </div>
      </div>

      {/* Emails List */}
      <div className="card">
        {isLoading ? (
          <div className="p-4">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-bg-secondary rounded"></div>
              ))}
            </div>
          </div>
        ) : emails.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No emails found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {emails.map((email) => (
              <div
                key={email.id}
                className="p-4 flex items-start gap-4 hover:bg-bg-secondary/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <h3 className="font-semibold text-text-primary">
                        {email.subject || 'No Subject'}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 mt-1">
                        {email.to && (
                          <p className="text-sm text-text-muted flex items-center gap-1">
                            <Send className="w-3 h-3" />
                            To: {email.to}
                          </p>
                        )}
                        {email.from && (
                          <p className="text-sm text-text-muted flex items-center gap-1">
                            <User className="w-3 h-3" />
                            From: {email.from}
                          </p>
                        )}
                      </div>
                    </div>
                    {email.status && (
                      <span className={`badge ${
                        email.status === 'sent' ? 'badge-success' :
                        email.status === 'pending' ? 'badge-warning' :
                        email.status === 'failed' ? 'badge-danger' :
                        'badge-gray'
                      }`}>
                        {email.status}
                      </span>
                    )}
                  </div>
                  {email.body && (
                    <p className="text-sm text-text-secondary line-clamp-2 mt-1">
                      {email.body}
                    </p>
                  )}
                  <p className="text-xs text-text-muted mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Created: {formatDate(email.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-text-muted">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-ghost"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-ghost"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
